import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Bolt as BoltIcon,
  Rocket as RocketIcon,
  WorkspacePremium as PremiumIcon,
} from "@mui/icons-material";
import subscriptionService from "../../../services/subscriptionService";

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansData, subData] = await Promise.all([
        subscriptionService.getPlans(),
        subscriptionService.getSubscription(),
      ]);
      setPlans(plansData);
      setCurrentSubscription(subData.subscription);
      setUsage(subData.usage);
    } catch (err) {
      setError("Failed to load pricing data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (plan) => {
    if (plan.name === "free") return;

    setProcessingPlan(plan.id);
    setError(null);

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setError("Payment gateway failed to load. Please try again.");
        setProcessingPlan(null);
        return;
      }

      const orderData = await subscriptionService.createOrder(plan.id, billingCycle);
      console.log('Order created:', orderData);

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Profile2Connect",
        description: `${plan.display_name} - ${billingCycle === "monthly" ? "Monthly" : "Yearly"} Plan`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const result = await subscriptionService.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              billing_cycle: billingCycle,
            });

            if (result.success) {
              setSuccess(`Successfully subscribed to ${plan.display_name}!`);
              fetchData();
            }
          } catch (err) {
            setError("Payment verification failed. Please contact support.");
          }
          setProcessingPlan(null);
        },
        modal: {
          ondismiss: () => {
            setProcessingPlan(null);
          },
        },
        prefill: {
          email: localStorage.getItem("userEmail") || "",
        },
        theme: {
          color: "#6366f1",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        setError(`Payment failed: ${response.error.description}`);
        setProcessingPlan(null);
      });
      razorpay.open();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create order");
      setProcessingPlan(null);
    }
  };

  const getFeatureValue = (plan, feature) => {
    const value = plan[feature];
    if (value === -1) return "Unlimited";
    if (typeof value === "boolean") return value;
    return value;
  };

  const features = [
    { key: "portfolio_templates", label: "Portfolio Templates", type: "number" },
    { key: "resume_templates", label: "Resume Templates", type: "number" },
    { key: "ai_resume_imports", label: "AI Resume Imports/month", type: "limit" },
    { key: "ai_description_improves", label: "AI Content Improves/month", type: "limit" },
    { key: "ai_design_generations", label: "AI Design Generations/month", type: "limit" },
    { key: "custom_domain", label: "Custom Domain", type: "boolean" },
    { key: "remove_branding", label: "Remove Branding", type: "boolean" },
    { key: "analytics", label: "Portfolio Analytics", type: "boolean" },
    { key: "priority_support", label: "Priority Support", type: "boolean" },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const currentPlanName = currentSubscription?.plan?.name || "free";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Unlock premium features to make your portfolio stand out
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
          <Typography
            variant="body1"
            fontWeight={billingCycle === "monthly" ? "bold" : "normal"}
            color={billingCycle === "monthly" ? "primary" : "text.secondary"}
          >
            Monthly
          </Typography>
          <Switch
            checked={billingCycle === "yearly"}
            onChange={(e) => setBillingCycle(e.target.checked ? "yearly" : "monthly")}
            color="primary"
          />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body1"
              fontWeight={billingCycle === "yearly" ? "bold" : "normal"}
              color={billingCycle === "yearly" ? "primary" : "text.secondary"}
            >
              Yearly
            </Typography>
            <Chip label="2 months free" size="small" color="success" />
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlanName === plan.name;
          const isPro = plan.name === "pro";
          const price = billingCycle === "monthly" ? plan.price_monthly : plan.price_yearly;

          return (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  overflow: "visible",
                  border: isPro ? "2px solid" : "1px solid",
                  borderColor: isPro ? "primary.main" : "divider",
                  transform: isPro ? "scale(1.05)" : "none",
                  boxShadow: isPro ? '0 20px 40px rgba(99, 102, 241, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  mt: isPro ? 2 : 0,
                  '&:hover': {
                    transform: isPro ? 'scale(1.07)' : 'scale(1.02)',
                    boxShadow: isPro ? '0 25px 50px rgba(99, 102, 241, 0.4)' : '0 8px 24px rgba(0,0,0,0.15)',
                  },
                  background: isPro ? 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)' : undefined,
                }}
              >
                {isPro && (
                  <Chip
                    icon={<StarIcon />}
                    label="Most Popular"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: -16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontWeight: 600,
                      px: 1,
                      zIndex: 1,
                    }}
                  />
                )}

                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {plan.display_name}
                  </Typography>

                  <Box display="flex" alignItems="baseline" mb={2}>
                    <Typography
                      variant="h3"
                      component="span"
                      sx={{
                        fontWeight: 800,
                        color: '#111827 !important',
                        fontSize: '3rem',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {price === 0 ? "Free" : `₹${price / 100}`}
                    </Typography>
                    {price > 0 && (
                      <Typography variant="body1" color="text.secondary" ml={1} fontWeight="medium">
                        /{billingCycle === "monthly" ? "mo" : "yr"}
                      </Typography>
                    )}
                  </Box>

                  {billingCycle === "yearly" && price > 0 && (
                    <Typography variant="body2" color="success.main" mb={1} fontWeight="medium">
                      Save ₹{((plan.price_monthly * 12 - plan.price_yearly) / 100).toFixed(0)}/year
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {plan.description}
                  </Typography>

                  <Button
                    variant={isCurrentPlan ? "outlined" : "contained"}
                    fullWidth
                    disabled={isCurrentPlan || processingPlan === plan.id}
                    onClick={() => handleSubscribe(plan)}
                    startIcon={
                      processingPlan === plan.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : plan.name === 'business' ? (
                        <RocketIcon />
                      ) : isPro ? (
                        <PremiumIcon />
                      ) : null
                    }
                    sx={{
                      mb: 3,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      borderRadius: '12px',
                      boxShadow: isCurrentPlan ? 'none' : isPro
                        ? '0 4px 14px rgba(99, 102, 241, 0.4)'
                        : plan.name === 'business'
                          ? '0 4px 14px rgba(234, 88, 12, 0.4)'
                          : 'none',
                      background: isCurrentPlan ? 'transparent' : isPro
                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                        : plan.name === 'business'
                          ? 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)'
                          : plan.name === 'free'
                            ? '#e5e7eb'
                            : undefined,
                      color: plan.name === 'free' && !isCurrentPlan ? '#374151' : undefined,
                      '&:hover': {
                        background: isCurrentPlan ? 'transparent' : isPro
                          ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                          : plan.name === 'business'
                            ? 'linear-gradient(135deg, #c2410c 0%, #b91c1c 100%)'
                            : plan.name === 'free'
                              ? '#d1d5db'
                              : undefined,
                        boxShadow: isCurrentPlan ? 'none' : isPro
                          ? '0 6px 20px rgba(99, 102, 241, 0.5)'
                          : plan.name === 'business'
                            ? '0 6px 20px rgba(234, 88, 12, 0.5)'
                            : 'none',
                      }
                    }}
                  >
                    {isCurrentPlan
                      ? "Current Plan"
                      : processingPlan === plan.id
                      ? "Processing..."
                      : plan.name === "free"
                      ? "Downgrade"
                      : `Get ${plan.display_name}`}
                  </Button>

                  <Divider sx={{ mb: 2 }} />

                  <List dense>
                    {features.map((feature) => {
                      const value = getFeatureValue(plan, feature.key);
                      const isAvailable = feature.type === "boolean" ? value : true;

                      return (
                        <ListItem key={feature.key} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {isAvailable ? (
                              <CheckIcon color="success" fontSize="small" />
                            ) : (
                              <CloseIcon color="disabled" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                color={isAvailable ? "text.primary" : "text.disabled"}
                              >
                                {feature.type === "limit"
                                  ? `${value === "Unlimited" ? "Unlimited" : value} ${feature.label.replace("/month", "")}`
                                  : feature.type === "number"
                                  ? `${value} ${feature.label}`
                                  : feature.label}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Current Usage Section */}
      {usage && currentSubscription && (
        <Box mt={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Your Usage This Month
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(usage).map(([key, data]) => (
              <Grid item xs={12} sm={4} key={key}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {key.replace(/_/g, " ").replace("ai ", "AI ")}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BoltIcon color="primary" />
                      <Typography variant="h6">
                        {data.used} / {data.unlimited ? "∞" : data.limit}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default PricingPage;
